// components/PricingCards.jsx
import { CheckCircle, Loader2 } from "lucide-react";
import { formatPersianNumber as toPersianPrice } from "../utils/persianNumber";

const PLAN_PRICES = {
    free: { monthly: 0, yearly: 0 },
    professional: { monthly: 99000, yearly: 990000 },
    premium: { monthly: 199000, yearly: 1990000 },
};

const PLANS = [
    {
        id: "free",
        title: "پلن پایه",
        subtitle: "برای شروع رایگان",
        badge: { label: "رایگان", className: "bg-primary_1 text-accent" },
        features: [
            "ساخت پروفایل رستوران، ساعات کاری و روزهای تعطیل",
            "۱ منوی دیجیتال با QR کد و لینک اختصاصی",
            "تا ۵ دسته‌بندی و ۳۰ آیتم در منو",
            "تصویر، توضیحات، قیمت و وضعیت موجود/ناموجود هر آیتم",
            "تعریف میزهای رستوران و مدیریت وضعیت آن‌ها",
            "ثبت سفارش آنلاین از روی میز (سبد خرید، داخل رستوران و بیرون‌بر)",
            "گالری تصاویر تا ۴ عکس",
            "مرکز راهنمایی و پشتیبانی از طریق تیکت",
        ],
    },
    {
        id: "professional",
        title: "پلن حرفه‌ای",
        subtitle: "برای رستوران‌های در حال رشد",
        popular: true,
        badge: { label: "محبوب‌ترین", className: "bg-secondary_1 text-secondary" },
        featuresIntro: "همه امکانات پلن پایه، به‌علاوه:",
        features: [
            "دسته‌بندی و آیتم نامحدود، چند منوی مختلف (صبحانه، ناهار، شام و مناسبتی)",
            "سیستم رزرو آنلاین میز، نقشه سالن و نمای زنده وضعیت میزها",
            "QR اختصاصی برای هر میز و مدیریت ظرفیت/لغو/یادآوری رزرو",
            "باشگاه مشتریان، پروفایل و تاریخچه مشتریان",
            "گالری تصاویر نامحدود",
            "آمار بازدید منو، اسکن QR و رزرو",
            "حذف برند بامیزبان از پایین منو",
            "اتصال به سیستم پیامکی و پشتیبانی آنلاین",
        ],
    },
    {
        id: "premium",
        title: "پلن بیزینس",
        subtitle: "برای مجموعه‌ها و شعبه‌های حرفه‌ای",
        business: true,
        badge: { label: "همه‌ی امکانات", className: "bg-pastel-peach text-white" },
        featuresIntro: "همه امکانات پلن حرفه‌ای، به‌علاوه:",
        features: [
            "مدیریت چند شعبه",
            "رزرو دستی، مسدودسازی میز و پیش‌پرداخت رزرو",
            "دسته‌بندی، امتیاز و باشگاه وفاداری مشتریان، کمپین‌های مشتریان",
            "گزارش‌ها و تحلیل پیشرفته فروش، پرفروش‌ترین غذاها و ساعات شلوغ",
            "چند کاربر مدیریتی با نقش و سطح دسترسی جدا",
            "دامنه اختصاصی و طراحی اختصاصی",
            "اتصال API، Webhook و POS",
            "پشتیبانی اولویت‌دار و مدیر حساب اختصاصی",
        ],
    },
];

export default function PricingCards({
    billing,
    activePlan,
    activeBillingCycle = "monthly",
    onSelect,
    dashboardMode = false,
    processingPlanId = null,
}) {
    return (
        <div className="grid md:grid-cols-3 gap-6 items-stretch">
            {PLANS.map((plan) => {
                const isActive =
                    activePlan === plan.id &&
                    (plan.id === "free" || activeBillingCycle === billing);
                const isSameActivePlanDifferentCycle =
                    dashboardMode && activePlan === plan.id && plan.id !== "free" && activeBillingCycle !== billing;
                const isProcessing = processingPlanId === plan.id;
                const isBlockedByOtherAction = Boolean(processingPlanId) && !isProcessing;
                const price = PLAN_PRICES[plan.id][billing];
                const monthlyEquivalent = billing === "yearly" && price > 0 ? Math.round(price / 12) : null;

                return (
                    <div
                        key={plan.id}
                        className={`
                            relative flex flex-col bg-white border rounded-xl p-6 min-h-[600px] transition
                            ${isActive ? "ring-2 ring-secondary border-secondary shadow-md" : "border-gray-200 hover:shadow-lg"}
                            ${!isActive && plan.popular ? "border-secondary shadow-sm" : ""}
                            ${!isActive && plan.business ? "border-pastel-peach shadow-sm" : ""}
                        `}
                    >
                        {/* badge */}
                        {plan.badge && (
                            <span className={`absolute top-4 left-4 text-xs px-3 py-1 rounded-md font-medium ${plan.badge.className}`}>
                                {plan.badge.label}
                            </span>
                        )}

                        {/* پلن فعلی */}
                        {isActive && (
                            <span className="absolute top-4 right-4 flex items-center gap-1 text-xs bg-secondary text-white px-3 py-1 rounded-md font-medium">
                                <CheckCircle size={14} />
                                پلن فعلی شما
                            </span>
                        )}

                        {/* header */}
                        <div className="text-center mb-6 mt-4">
                            <h3 className="text-xl font-bold text-accent">{plan.title}</h3>
                            <p className="text-sm text-gray-500 mt-1">{plan.subtitle}</p>
                        </div>

                        {/* price */}
                        <div className="text-center mb-6">
                            <p className="text-3xl font-extrabold text-accent">
                                {price === 0 ? "رایگان" : `${toPersianPrice(price)} تومان`}
                            </p>
                            {price > 0 && (
                                <span className="text-sm text-gray-500">
                                    {billing === "monthly" ? "ماهانه" : "سالانه"}
                                </span>
                            )}
                            {monthlyEquivalent !== null && (
                                <p className="text-xs text-secondary mt-1 font-medium">
                                    معادل {toPersianPrice(monthlyEquivalent)} تومان در ماه — ۲ ماه رایگان
                                </p>
                            )}
                        </div>

                        {/* features */}
                        <div className="flex-1">
                            {plan.featuresIntro && (
                                <p className="text-xs text-gray-500 mb-3">{plan.featuresIntro}</p>
                            )}
                            <ul className="space-y-3">
                                {plan.features.map((feature, i) => (
                                    <li key={i} className="flex items-center gap-2 text-sm text-gray-700">
                                        <CheckCircle size={16} className="text-secondary shrink-0" />
                                        {feature}
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {/* button */}
                        <button
                            disabled={isActive || isProcessing || isBlockedByOtherAction}
                            onClick={() => onSelect(plan.id)}
                            className={`
                                mt-6 w-full py-3 rounded-lg font-semibold text-sm transition flex items-center justify-center gap-2
                                ${isActive
                                    ? "bg-secondary_2 text-gray-500 cursor-not-allowed"
                                    : isBlockedByOtherAction
                                        ? "bg-secondary_2 text-gray-400 cursor-not-allowed"
                                        : "bg-secondary text-white hover:opacity-90"
                                }
                            `}
                        >
                            {isProcessing && <Loader2 size={16} className="animate-spin" />}
                            {isActive
                                ? "پلن فعلی شما"
                                : isProcessing
                                    ? "در حال فعال‌سازی..."
                                    : isSameActivePlanDifferentCycle
                                        ? `تغییر به دوره ${billing === "yearly" ? "سالانه" : "ماهانه"}`
                                        : plan.id === "free"
                                            ? "بازگشت به پلن رایگان"
                                            : "انتخاب این پلن"}
                        </button>

                        {isSameActivePlanDifferentCycle && (
                            <p className="text-xs text-center text-secondary mt-2 font-medium">
                                هم‌اکنون با دوره {activeBillingCycle === "yearly" ? "سالانه" : "ماهانه"} فعال است
                            </p>
                        )}

                        {dashboardMode && !isActive && !isSameActivePlanDifferentCycle && (
                            <p className="text-xs text-center text-gray-400 mt-2">
                                ارتقا یا تغییر پلن بدون از دست رفتن اطلاعات
                            </p>
                        )}
                    </div>
                );
            })}
        </div>
    );
}

export { PLANS as SUBSCRIPTION_PLAN_DEFINITIONS, PLAN_PRICES as SUBSCRIPTION_PLAN_PRICES };
